import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useTheme,
    useMediaQuery,
    Alert,
    Snackbar,
    LinearProgress,
    Avatar,
    Fade,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';
import { Invoice, NewInvoice, useGetItemsQuery, usePostInvoiceMutation } from '../../redux/residentPortalApi';
import { Field, FieldArray, Form, Formik, useFormikContext } from 'formik';
import createGenericInvoiceModel, { INVOICE_DESCRIPTION_OPTIONS } from './models/createGenericInvoiceModel';
import {
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    CalendarMonth as CalendarIcon,
    Home as HomeIcon,
    AttachMoney as AttachMoneyIcon,
    Receipt as ReceiptIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    Description as DescriptionIcon,
    Payments as PaymentsIcon,
    Category as CategoryIcon,
} from '@mui/icons-material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import developmentsData from '../../resident-portal';
import { formatCurrency } from "../../utils/currencyUtil";
import { Link } from 'react-router-dom';

const {
    formField: {
        totalCost,
        development,
        dueDate,
        type,
        phase,
        invoiceDescription,
    },
} = createGenericInvoiceModel;

const formatPropertyLabel = (option: { development: string; phase: string; type: string }) => {
    const capitalizeWords = (str: string) =>
        str ? str.replace(/\b\w+/g, (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) : '';

    const development = option.development ? capitalizeWords(option.development.replace(/-/g, ' ')) : '';
    const phase = option.phase ? capitalizeWords(option.phase) : '';
    const type = option.type ? capitalizeWords(option.type) : '';

    return `${development} ${phase} ${type}`.trim();
};

const CreateGenericInvoiceForm = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { values, setFieldValue } = useFormikContext<{
        items: Array<{
            description: string;
            quantity: number;
            unitPrice: number;
            totalCostPerItem: number;
        }>;
        totalCost: number;
        development: string;
        type: string;
        phase: string;
        dueDate: Date | null;
        invoiceDescription: string;
    }>();
    const { data: items = [], isLoading: itemsLoading } = useGetItemsQuery();

    useEffect(() => {
        const total = calculateTotal(values.items);

        if (values.totalCost !== total) {
            setFieldValue('totalCost', total);
        }
    }, [values.items, values.totalCost, setFieldValue]);

    function calculateTotal(items: any): number {
        return items.reduce((acc: number, item: any) => {
            const totalPerItem = Number(item.quantity) * Number(item.unitPrice) || 0;
            return acc + totalPerItem;
        }, 0);
    }

    const propertyMetrics = developmentsData.propertyMetrics;

    const [selectedProperty, setSelectedProperty] = useState<{
        development: string;
        type: string;
        phase: string;
    }>({
        development: '',
        type: '',
        phase: '',
    });

    const handlePropertyMetricsChange = (event: React.SyntheticEvent, newValue: {
        development: string;
        type: string;
        phase: string;
    } | null) => {
        if (newValue) {
            const { development, type, phase } = newValue;

            setFieldValue('development', development);
            setFieldValue('type', type);
            setFieldValue('phase', phase);

            setSelectedProperty({
                development,
                type,
                phase,
            });

            const filtered = items.filter((item) =>
                item.development === development &&
                item.type === type &&
                item.phase === phase
            );

            setFieldValue('items', filtered.map(item => ({
                description: item.description || '',
                quantity: 1,
                unitPrice: item.unitPrice,
                totalCostPerItem: item.unitPrice,
            })));

        } else {
            setSelectedProperty({
                development: '',
                type: '',
                phase: ''
            });

            setFieldValue('items', []);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                {/* Property Selection Card */}
                <Fade in={true} timeout={500}>
                    <Card
                        sx={{
                            mb: 4,
                            borderRadius: 3,
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                            overflow: 'visible',
                            border: '1px solid rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2.5,
                                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.primary.light,
                                    color: 'white',
                                    mr: 2
                                }}
                            >
                                <HomeIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight="600">
                                Property Information
                            </Typography>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={propertyMetrics}
                                        getOptionLabel={formatPropertyLabel}
                                        onChange={handlePropertyMetricsChange}
                                        value={selectedProperty}
                                        isOptionEqualToValue={(option, value) =>
                                            option.development === value.development &&
                                            option.type === value.type &&
                                            option.phase === value.phase
                                        }
                                        fullWidth
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Property"
                                                variant="outlined"
                                                placeholder="Search by development, phase or type"
                                                required
                                                error={!selectedProperty.development}
                                                helperText={!selectedProperty.development ? "Property selection is required" : ""}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                    <Field {...development} type="hidden" value={selectedProperty.development} />
                                    <Field {...type} type="hidden" value={selectedProperty.type} />
                                    <Field {...phase} type="hidden" value={selectedProperty.phase} />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <DesktopDatePicker
                                        {...dueDate}
                                        label="Due Date"
                                        format="dd/MM/yyyy"
                                        value={values.dueDate}
                                        onChange={(value) => {
                                            if (value) {
                                                const utcDate = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
                                                setFieldValue('dueDate', utcDate);
                                            } else {
                                                setFieldValue('dueDate', null);
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                ...dueDate,
                                                fullWidth: true,
                                                required: true,
                                                error: !values.dueDate,
                                                helperText: !values.dueDate ? "Due date is required" : "",
                                                InputProps: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CalendarIcon color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth required error={!values.invoiceDescription}>
                                        <InputLabel id="invoice-description-label">Invoice Description</InputLabel>
                                        <Select
                                            labelId="invoice-description-label"
                                            label="Invoice Description"
                                            value={values.invoiceDescription}
                                            onChange={(e) => setFieldValue('invoiceDescription', e.target.value)}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <CategoryIcon color="primary" />
                                                </InputAdornment>
                                            }
                                            sx={{
                                                borderRadius: 2
                                            }}
                                        >
                                            {INVOICE_DESCRIPTION_OPTIONS.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {selectedProperty.development && (
                                <Box mt={3}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            bgcolor: 'rgba(25, 118, 210, 0.08)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(25, 118, 210, 0.2)'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <InfoIcon color="primary" sx={{ mr: 1.5 }} />
                                            <Typography variant="body1">
                                                Selected property: <strong>{formatPropertyLabel(selectedProperty)}</strong>
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Box>
                            )}

                            <Field {...totalCost} type="hidden" />
                        </CardContent>
                    </Card>
                </Fade>

                {/* Invoice Items Card */}
                <Fade in={true} timeout={700}>
                    <Card
                        sx={{
                            mb: 4,
                            borderRadius: 3,
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                            overflow: 'visible',
                            border: '1px solid rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2.5,
                                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.secondary.light,
                                    color: 'white',
                                    mr: 2
                                }}
                            >
                                <ReceiptIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight="600">
                                Invoice Items
                            </Typography>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            {itemsLoading ? (
                                <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                                    <LinearProgress color="secondary" />
                                    <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                                        Loading items...
                                    </Typography>
                                </Box>
                            ) : (
                                <FieldArray name="items">
                                    {({remove}) => (
                                        <>
                                            {values.items.length === 0 ? (
                                                <Box
                                                    sx={{
                                                        textAlign: 'center',
                                                        py: 5,
                                                        px: 3,
                                                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                                                        borderRadius: 2,
                                                        border: '1px dashed rgba(0, 0, 0, 0.15)'
                                                    }}
                                                >
                                                    <DescriptionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                                        No Items Added
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Please select a property first or add items manually.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Paper
                                                    elevation={0}
                                                    sx={{
                                                        mt: 2,
                                                        mb: 3,
                                                        borderRadius: 2,
                                                        overflow: 'hidden',
                                                        border: '1px solid rgba(0, 0, 0, 0.08)'
                                                    }}
                                                >
                                                    <Box sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: isMobile ? '1fr 100px 50px' : '1fr 150px 150px 80px',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                        p: 2.5,
                                                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                                                    }}>
                                                        <Typography variant="subtitle2" fontWeight="600">Description</Typography>
                                                        {!isMobile && <Typography variant="subtitle2" fontWeight="600">Unit Price</Typography>}
                                                        <Typography variant="subtitle2" fontWeight="600">Total</Typography>
                                                        <Typography variant="subtitle2" fontWeight="600" align="center">Action</Typography>
                                                    </Box>

                                                    {values.items.map((item, index: number) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: isMobile ? '1fr 100px 50px' : '1fr 150px 150px 80px',
                                                                p: 2.5,
                                                                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.01)' },
                                                                transition: 'background-color 0.2s'
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography variant="body1" fontWeight="500">
                                                                    {item.description || 'No description'}
                                                                </Typography>
                                                                {isMobile && (
                                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                        Unit Price: {formatCurrency(item.unitPrice || 0)}
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {!isMobile && (
                                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <AttachMoneyIcon fontSize="small" color="action" sx={{ mr: 0.5, opacity: 0.7 }} />
                                                                    {formatCurrency(item.unitPrice ?? 0)}
                                                                </Typography>
                                                            )}

                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="600"
                                                                color="primary"
                                                                sx={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <AttachMoneyIcon fontSize="small" color="primary" sx={{ mr: 0.5, opacity: 0.8 }} />
                                                                {formatCurrency((Number(item.quantity ?? 0) * Number(item.unitPrice ?? 0)))}
                                                            </Typography>

                                                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                <Tooltip title="Remove Item">
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => remove(index)}
                                                                        sx={{
                                                                            bgcolor: 'rgba(211, 47, 47, 0.04)',
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(211, 47, 47, 0.12)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Paper>
                                            )}

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    alignItems: 'center',
                                                    mt: 2
                                                }}
                                            >
                                                <Paper
                                                    elevation={3}
                                                    sx={{
                                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                        color: 'white',
                                                        padding: '16px 24px',
                                                        borderRadius: 3,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        boxShadow: '0 8px 16px rgba(25, 118, 210, 0.2)'
                                                    }}
                                                >
                                                    <PaymentsIcon fontSize="medium" />
                                                    <Box>
                                                        <Typography variant="caption" sx={{ opacity: 0.9, color: 'white' }}>
                                                            TOTAL AMOUNT
                                                        </Typography>
                                                        <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, color: 'white' }}>
                                                            {formatCurrency(values.totalCost || 0)}
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        </>
                                    )}
                                </FieldArray>
                            )}
                        </CardContent>
                    </Card>
                </Fade>
            </Box>
        </LocalizationProvider>
    );
};

const CreateGenericInvoice = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    interface FormValues {
        items: Array<{
            description: string;
            quantity: number;
            unitPrice: number;
            totalCostPerItem: number;
        }>;
        dueDate: Date | null;
        totalCost: number;
        development: string;
        type: string;
        phase: string;
        invoiceDescription: string;
    }

    const initialValues: FormValues = {
        items: [],
        dueDate: null,
        totalCost: 0,
        development: '',
        type: '',
        phase: '',
        invoiceDescription: '',
    };

    const [postInvoice] = usePostInvoiceMutation();

    const handleCloseSnackbar = () => {
        setShowSuccess(false);
        setShowError(false);
    };

    const theme = useTheme();

    return (
        <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{
                mb: 4,
                p: 3,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                color: 'white'
            }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} md={7}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                                sx={{
                                    bgcolor: 'white',
                                    color: theme.palette.primary.main,
                                    width: 56,
                                    height: 56,
                                    mr: 2,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                }}
                            >
                                <ReceiptIcon fontSize="large" />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'white' }}>
                                    Create Generic Invoice
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.9 }}>
                                    Create a once-off invoice not limited to a specific invoice type
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                        <Link to="/admin/generic-invoices" style={{ textDecoration: 'none' }}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                sx={{
                                    borderRadius: 2,
                                    mr: 1,
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.3)'
                                    }
                                }}
                            >
                                Back to Generic Invoices
                            </Button>
                        </Link>
                    </Grid>
                </Grid>
            </Box>

            <Formik
                initialValues={initialValues}
                onSubmit={(values: FormValues, actions) => {
                    setIsSubmitting(true);

                    // Validate form
                    if (!values.development || !values.dueDate || !values.invoiceDescription || values.items.length === 0) {
                        setErrorMessage('Please fill in all required fields and add at least one item');
                        setShowError(true);
                        setIsSubmitting(false);
                        return;
                    }

                    const items = values.items.map((item) => ({
                        description: item.description,
                        quantity: typeof item.quantity === 'string'
                            ? parseInt(item.quantity as unknown as string, 10) || 0
                            : Math.floor(item.quantity),
                        unitPrice: typeof item.unitPrice === 'string'
                            ? parseInt(item.unitPrice as unknown as string, 10) || 0
                            : Math.floor(item.unitPrice),
                        procured: false,
                        totalCostPerItem: item.totalCostPerItem,
                    }));

                    const invoiceData: NewInvoice = {
                        development: values.development,
                        dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
                        type: values.type,
                        phase: values.phase,
                        recurring: false,
                        recurringPeriod: 0,
                        totalCost: values.totalCost,
                        items: items,
                        isGeneric: true,
                        invoiceDescription: values.invoiceDescription,
                    };

                    postInvoice({ newInvoice: invoiceData }).unwrap()
                        .then((response: Invoice) => {
                            console.log('Generic invoice submitted successfully:', response);
                            setShowSuccess(true);
                            actions.setSubmitting(false);
                            actions.resetForm();
                            setIsSubmitting(false);
                        })
                        .catch((error) => {
                            console.error('Error submitting generic invoice:', error);
                            setErrorMessage('Failed to create invoice. Please try again.');
                            setShowError(true);
                            actions.setSubmitting(false);
                            setIsSubmitting(false);
                        });
                }}
            >
                {() => (
                    <Form>
                        <CreateGenericInvoiceForm />

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 4,
                                pt: 3,
                                borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                            }}
                        >
                            <Link to="/admin/generic-invoices" style={{ textDecoration: 'none' }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1.2,
                                        borderWidth: 2,
                                        '&:hover': {
                                            borderWidth: 2
                                        }
                                    }}
                                >
                                    Back to Generic Invoices
                                </Button>
                            </Link>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={isSubmitting}
                                sx={{
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.2,
                                    position: 'relative',
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <CircularProgress
                                            size={24}
                                            color="inherit"
                                            sx={{
                                                position: 'absolute',
                                                left: '50%',
                                                marginLeft: '-12px'
                                            }}
                                        />
                                        <span style={{ visibility: 'hidden' }}>Creating Invoice...</span>
                                    </>
                                ) : 'Create Invoice'}
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>

            {/* Success Snackbar */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    variant="filled"
                    icon={<CheckCircleIcon />}
                    sx={{
                        width: '100%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" color={'white'}>
                            Invoice Created Successfully!
                        </Typography>
                        <Typography variant="body2">
                            The generic invoice has been created and will be visible to property owners.
                        </Typography>
                    </Box>
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={showError}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="error"
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        '& .MuiAlert-icon': {
                            fontSize: '1.5rem'
                        }
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Error Creating Invoice
                        </Typography>
                        <Typography variant="body2">
                            {errorMessage}
                        </Typography>
                    </Box>
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreateGenericInvoice;
